// js/accounts/bootstrap.js
// Wires up the Nostr stack on top of the existing Appwrite/PocketBase setup.
//
// Strategy:
//   - Always available: PocketBase syncManager (already self-registers in
//     pocketbase.js). The sync-router proxy routes to it by default.
//   - Lazy: the Nostr stack is only loaded when a Nostr session is detected
//     in localStorage at startup, OR when the user opens the modal.
//   - Coexistence: if both an Appwrite session AND a stored Nostr account
//     exist, prefer whichever was last active per `LAST_ACTIVE_SESSION_KEY`.
//
// This module is imported once from app.js and runs on import.

import { authManager } from './auth.js';
import { lastActiveSessionMarker, registerNostrAuthAccessor, setLastActiveSessionMarker } from './session.js';
import { registerNostrSyncProvider } from './sync-router.js';

const hasStoredNostrAccount = () => {
    try {
        const raw = localStorage.getItem('accounts');
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length > 0 && !!localStorage.getItem('active-account');
    } catch {
        return false;
    }
};

let _loadPromise = null;

const loadNostrStack = () => {
    if (_loadPromise) return _loadPromise;

    _loadPromise = (async () => {
        const [{ nostrAuthManager }, { nostrSync, getNostrSync }, { initNostrSettingsPanel }] =
            await Promise.all([
                import('./nostr/auth-manager.js'),
                import('./nostr/sync.js'),
                import('./nostr/ui-settings.js'),
            ]);

        registerNostrAuthAccessor(() => nostrAuthManager);
        registerNostrSyncProvider(getNostrSync);

        await nostrAuthManager.init();
        initNostrSettingsPanel();

        nostrAuthManager.onAuthStateChanged((user) => {
            if (user) {
                setLastActiveSessionMarker('nostr');
                nostrSync.onAuthStateChanged(user).catch((e) =>
                    console.error('[NOSTR-BOOTSTRAP] sync onAuthStateChanged failed:', e)
                );
            } else {
                nostrSync.onAuthStateChanged(null).catch(() => {});
            }
        });

        return { nostrAuthManager, nostrSync };
    })();

    return _loadPromise;
};

// Track the Appwrite session-active marker so the resolver below knows
// which side won the coexistence check.
authManager.onAuthStateChanged((user) => {
    if (user) {
        // Only overwrite the marker if it isn't already pointing to nostr,
        // since `auth.get()` resolves slightly later than the Nostr engine.
        const current = lastActiveSessionMarker();
        if (current !== 'nostr') setLastActiveSessionMarker('appwrite');
    }
});

const resolveCoexistence = async () => {
    const marker = lastActiveSessionMarker();
    const hasNostr = hasStoredNostrAccount();
    const appwriteUser = authManager.user;

    if (hasNostr && marker === 'nostr') {
        // Nostr was last active — load stack to restore session.
        // If Appwrite ALSO has an active session, the Nostr login takes
        // precedence; `nostrSync.onAuthStateChanged` will route writes
        // away from PocketBase via the sync-router.
        await loadNostrStack();
        return;
    }

    if (hasNostr && marker !== 'appwrite' && !appwriteUser) {
        // No clear winner, but only Nostr has stored credentials.
        await loadNostrStack();
        return;
    }

    // Otherwise stay on the lightweight Appwrite-only path. The Nostr
    // stack will be loaded lazily when the user clicks "Connect with Nostr".
};

resolveCoexistence().catch((e) => console.error('[NOSTR-BOOTSTRAP] resolve failed:', e));

// Expose the loader so the auth.js click handler can trigger it on demand.
export { loadNostrStack };
